import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

export class NotificationsRealtimeClient {
  constructor(hubUrl, getToken) {
    this.hubUrl = hubUrl;
    this.getToken = getToken;
    this.connection = null;
    // Task 13.3: handlers de checklist_item_updated y grupos de caso suscritos.
    this._checklistHandlers = new Set();
    this._joinedCases = new Set();
    // Templates: handlers de template_generation_changed + flag de suscripción al grupo global.
    this._templateHandlers = new Set();
    this._joinedTemplates = false;
    // Negociación Administrativa: handlers de admin_negotiation_updated + flag de grupo global.
    this._adminNegotiationHandlers = new Set();
    this._joinedAdminNegotiation = false;
  }

  async connect({ onReceived, onConnected, onDisconnected, onError }) {
    if (this.connection) return;

    this.connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => this.getToken() || '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.connection.on('notification:received', (payload) => {
      if (onReceived) onReceived(payload);
    });

    // Task 13.3: evento de actualización de item del checklist (EEPP BETA).
    this.connection.on('checklist_item_updated', (payload) => {
      this._checklistHandlers.forEach((h) => {
        try {
          h(payload);
        } catch (e) {
          console.error('checklist_item_updated handler error', e);
        }
      });
    });

    // Templates: cambio de estado de generación (lista/detalle EEPP).
    this.connection.on('template_generation_changed', (payload) => {
      this._templateHandlers.forEach((h) => {
        try {
          h(payload);
        } catch (e) {
          console.error('template_generation_changed handler error', e);
        }
      });
    });

    // Negociación Administrativa: cambio en una viabilidad/documento/aprobación (listas + detalle).
    this.connection.on('admin_negotiation_updated', (payload) => {
      this._adminNegotiationHandlers.forEach((h) => {
        try {
          h(payload);
        } catch (e) {
          console.error('admin_negotiation_updated handler error', e);
        }
      });
    });

    this.connection.onreconnected(() => {
      // Re-suscribir a los grupos tras reconexión (SignalR pierde los grupos).
      this._rejoinAllCases();
      if (this._joinedTemplates) this._rejoinTemplates();
      if (this._joinedAdminNegotiation) this._rejoinAdminNegotiation();
      if (onConnected) onConnected();
    });

    this.connection.onclose(() => {
      if (onDisconnected) onDisconnected();
    });

    try {
      await this.connection.start();
      this._rejoinAllCases();
      if (this._joinedTemplates) this._rejoinTemplates();
      if (this._joinedAdminNegotiation) this._rejoinAdminNegotiation();
      if (onConnected) onConnected();
    } catch (e) {
      if (onError) onError(e);
      this.connection = null;
    }
  }

  async disconnect() {
    if (!this.connection) return;

    const c = this.connection;
    this.connection = null;
    this._joinedCases.clear();
    this._joinedTemplates = false;
    this._joinedAdminNegotiation = false;

    try {
      await c.stop();
    } catch {
      console.error('Error disconnecting from notifications hub');
    }
  }

  // ─── Task 13.3: suscripción por caso ──────────────────────────────────

  /** Registra un handler de checklist_item_updated. Devuelve fn para desregistrar. */
  onChecklistItemUpdated(handler) {
    this._checklistHandlers.add(handler);
    return () => this._checklistHandlers.delete(handler);
  }

  /** Une la conexión al grupo del caso (invoca JoinCaseGroup en el hub). Idempotente. */
  async joinCase(caseId) {
    if (caseId == null) return;
    this._joinedCases.add(caseId);
    if (this.connection?.state === HubConnectionState.Connected) {
      try {
        await this.connection.invoke('JoinCaseGroup', Number(caseId));
      } catch (e) {
        console.error('JoinCaseGroup failed', e);
      }
    }
    // Si aún no está conectado, _rejoinAllCases lo hará al conectar.
  }

  /** Deja el grupo del caso (invoca LeaveCaseGroup). */
  async leaveCase(caseId) {
    if (caseId == null) return;
    this._joinedCases.delete(caseId);
    if (this.connection?.state === HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveCaseGroup', Number(caseId));
      } catch (e) {
        console.error('LeaveCaseGroup failed', e);
      }
    }
  }

  _rejoinAllCases() {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    this._joinedCases.forEach((caseId) => {
      this.connection.invoke('JoinCaseGroup', Number(caseId)).catch((e) => {
        console.error('JoinCaseGroup (rejoin) failed', e);
      });
    });
  }

  // ─── Templates: suscripción al grupo global de generación ─────────────

  /** Registra un handler de template_generation_changed. Devuelve fn para desregistrar. */
  onTemplateGenerationChanged(handler) {
    this._templateHandlers.add(handler);
    return () => this._templateHandlers.delete(handler);
  }

  /** Une la conexión al grupo global de Templates (JoinTemplatesGroup). Idempotente. */
  async joinTemplates() {
    this._joinedTemplates = true;
    if (this.connection?.state === HubConnectionState.Connected) {
      try {
        await this.connection.invoke('JoinTemplatesGroup');
      } catch (e) {
        console.error('JoinTemplatesGroup failed', e);
      }
    }
  }

  /** Deja el grupo de Templates (LeaveTemplatesGroup). */
  async leaveTemplates() {
    this._joinedTemplates = false;
    if (this.connection?.state === HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveTemplatesGroup');
      } catch (e) {
        console.error('LeaveTemplatesGroup failed', e);
      }
    }
  }

  _rejoinTemplates() {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    this.connection.invoke('JoinTemplatesGroup').catch((e) => {
      console.error('JoinTemplatesGroup (rejoin) failed', e);
    });
  }

  // ─── Negociación Administrativa: suscripción al grupo global ──────────────
  // Ref-counting: la lista de Documentos y el modal de detalle se montan a la vez,
  // así que ambos se suscriben al mismo grupo. Unimos la conexión al grupo solo
  // con el PRIMER handler y la sacamos solo cuando se desregistra el ÚLTIMO, atado
  // al tamaño de _adminNegotiationHandlers. Así cerrar el modal (queda ≥1 handler)
  // NO saca a la página del grupo.
  // (Templates usa el patrón simple sin ref-count porque lista/detalle son rutas
  // separadas, nunca co-montadas.)

  /** Registra un handler de admin_negotiation_updated. Devuelve fn para desregistrar. */
  onAdminNegotiationUpdated(handler) {
    this._adminNegotiationHandlers.add(handler);
    return () => this._adminNegotiationHandlers.delete(handler);
  }

  /** Une la conexión al grupo global de NA (JoinAdminNegotiationGroup). Ref-counted:
   *  solo invoca el join real en la 1ª suscripción; idempotente en las siguientes. */
  async joinAdminNegotiation() {
    // Solo el primer handler dispara el join real al grupo.
    if (this._joinedAdminNegotiation) return;
    this._joinedAdminNegotiation = true;
    if (this.connection?.state === HubConnectionState.Connected) {
      try {
        await this.connection.invoke('JoinAdminNegotiationGroup');
      } catch (e) {
        console.error('JoinAdminNegotiationGroup failed', e);
      }
    }
  }

  /** Deja el grupo de NA (LeaveAdminNegotiationGroup). Ref-counted: solo saca la
   *  conexión del grupo cuando ya no queda ningún handler suscrito. */
  async leaveAdminNegotiation() {
    // Solo el último handler desregistrado dispara el leave real; si aún queda
    // algún suscriptor (p.ej. la lista tras cerrar el modal), no salimos del grupo.
    if (this._adminNegotiationHandlers.size > 0) return;
    if (!this._joinedAdminNegotiation) return;
    this._joinedAdminNegotiation = false;
    if (this.connection?.state === HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveAdminNegotiationGroup');
      } catch (e) {
        console.error('LeaveAdminNegotiationGroup failed', e);
      }
    }
  }

  _rejoinAdminNegotiation() {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    this.connection.invoke('JoinAdminNegotiationGroup').catch((e) => {
      console.error('JoinAdminNegotiationGroup (rejoin) failed', e);
    });
  }
}
